import { useEffect, useState } from "react";
import { T } from "src/apps/components/i18n/Translation";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { PinInput } from "src/apps/components/pinInput/PinInput";
import styled from "styled-components";
import { ResendTwoFactor } from "./ResendTwoFactor";
import { Button } from "src/apps/components/button/Button";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { BoldHeader1, Text } from "src/apps/components/typography/Texts";
import { useNavigate } from "react-router-dom";
import { useGetToken } from "src/apps/hooks/useGetToken";
import { useAuth } from "src/apps/hooks/useAuth";
import { useSendTwoFactor } from "src/apps/hooks/useSendTwoFactor";
import { FadeInAnimation } from "src/apps/components/fadeIn/FadeInAnimation";
import { FormContainer } from "./FormContainer";
import { useTheme } from "src/apps/providers/ThemeStore";
import { LoginTypes } from "../../../../contexts/auth/application/AuthController";
import { useGetUserLink } from "src/apps/hooks/useGetUserLink";
import { ImArrowRight2 } from "react-icons/im";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";

const GO_BACK_DELAY_MS = 30 * 1000;

const DOCUMENTATION_AREA_LOGIN_OTP_TITLE = "documentation_area.login.otp.title";
const DOCUMENTATION_AREA_LOGIN_OTP_SUBTITLE = "documentation_area.login.otp.subtitle";
const DOCUMENTATION_AREA_LOGIN_OTP_SUBMIT_BUTTON_TEXT =
  "documentation_area.login.otp.submit_button_text";
const DOCUMENTATION_AREA_LOGIN_OTP_GOBACK_BUTTON_TEXT =
  "documentation_area.login.otp.goback_button_text";

const PIN_INPUT_LENGTH = 6;

export const OTPForm = () => {
  const [showGoBack, setShowGoBack] = useState(false);
  const navigate = useNavigate();
  const { getToken, isGetTokenPending } = useGetToken();
  const { mutate: resendOTP } = useSendTwoFactor();
  const { t } = useTranslations();
  const { loginEmail, setOTPSubmitError, otpSubmitError } = useAuth();
  const { blackColor } = useTheme();
  const { userLink } = useGetUserLink();
  const { sendEvent } = useSendAnalyticEvent();

  const [pinInputValue, setPinInputValue] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setShowGoBack(true), GO_BACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OTP_SCREEN_OPENED);
  }, []);

  useEffect(() => {
    if (otpSubmitError) {
      sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OTP_SCREEN_ERROR);
    }
  }, [otpSubmitError]);

  const onSubmit = () => {
    getToken(
      { code: pinInputValue, email: loginEmail },
      {
        onSuccess: () => {
          sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OTP_SCREEN_CORRECT);
        },
      },
    );
  };

  const setInput = (value: string) => {
    setOTPSubmitError("");
    setPinInputValue(value);
  };

  const goBack = () => {
    navigate(-1);
  };

  const onResendOTPClick = () => {
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_OTP_SCREEN_RESEND_CODE);
    resendOTP(loginEmail, {
      onSuccess: () => {
        setPinInputValue("");
        setOTPSubmitError("");
      },
    });
  };

  return (
    <FadeInAnimation duration={0.7}>
      <FormContainer>
        <Icon size={ICON_SIZES.s30} path={ICON_PATHS.SHIELD_CHECK} color={blackColor} />
        <BoldHeader1>
          <T k={DOCUMENTATION_AREA_LOGIN_OTP_TITLE} />
        </BoldHeader1>
        <Text className="text mp-mask">
          <T k={DOCUMENTATION_AREA_LOGIN_OTP_SUBTITLE} />
          <strong>{" " + loginEmail}</strong>
        </Text>
        <OTPContainer>
          <PinContainer>
            <PinInput
              length={PIN_INPUT_LENGTH}
              value={pinInputValue}
              onChange={setInput}
              error={t(otpSubmitError)}
            />
          </PinContainer>
          <ResendTwoFactor resendTwoFactor={onResendOTPClick} loginType={LoginTypes.EMAIL_CODE} />
        </OTPContainer>
        <ButtonsContainer>
          <Button
            onClick={onSubmit}
            disabled={pinInputValue.length !== PIN_INPUT_LENGTH}
            text={t(DOCUMENTATION_AREA_LOGIN_OTP_SUBMIT_BUTTON_TEXT)}
            rightIcon={<ImArrowRight2 />}
            isLoading={isGetTokenPending}
          />
          {!userLink && showGoBack ? (
            <Button
              onClick={goBack}
              variant="ghost"
              text={t(DOCUMENTATION_AREA_LOGIN_OTP_GOBACK_BUTTON_TEXT)}
            />
          ) : null}
        </ButtonsContainer>
      </FormContainer>
    </FadeInAnimation>
  );
};

const OTPContainer = styled.div``;

const PinContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;
